import React from 'react';
import { Alert, Button, Descriptions, Input, Modal, Select, Space, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import moment from 'moment';
import MainContent from '@/layouts/MainContent';
import { DEFAULT_DISPLAY_DATE_TIME_FORMAT, formatDisplayDate } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { CreditNoteListItemModel, CreditNoteMovementModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'creditNotes_Title';
const STATUSES = ['active', 'partial', 'used', 'expired', 'cancelled'];
const STATUS_COLORS: Record<string, string> = { active: 'green', partial: 'blue', used: 'default', expired: 'red', cancelled: 'default' };

const formatDate = (v?: string | Date) => (v ? moment(v).format(DEFAULT_DISPLAY_DATE_TIME_FORMAT) : '');
const formatDay = (v?: string | Date) => formatDisplayDate(v);

/**
 * Avoirs de la compagnie (P43), en lecture seule : émis (rendu d'une modification ou d'une annulation), utilisés aux
 * ventes, soldes et échéances ; détail des mouvements. Permission « Consulter les avoirs ».
 */
export default function CreditNotesPage() {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<string>();
  const [number, setNumber] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [filters, setFilters] = React.useState<{ number?: string; phoneNumber?: string }>({});
  const [page, setPage] = React.useState(1);
  const [detailId, setDetailId] = React.useState<string>();
  const { data, isLoading } = useQuery(['company_credit_notes', status, filters, page], () =>
    apiSdk.CreditNoteService.creditNotes({ status, ...filters, pageIndex: page, pageSize: 50 }),
  );
  const { data: detail, isLoading: detailLoading } = useQuery(
    ['company_credit_note', detailId],
    () => apiSdk.CreditNoteService.creditNotes1({ id: detailId! }),
    { enabled: !!detailId },
  );

  const search = () => {
    setPage(1);
    setFilters({ number: number.trim() || undefined, phoneNumber: phone.replace(/\D/g, '') || undefined });
  };

  const statusTag = (v?: string) => <Tag color={STATUS_COLORS[v || ''] || 'default'}>{t(`creditNotes_Status_${v}`)}</Tag>;

  const columns = [
    { title: t('creditNotes_Number'), dataIndex: 'number' },
    { title: t('creditNotes_Holder'), dataIndex: 'holderName' },
    { title: t('creditNotes_Phone'), dataIndex: 'holderPhone', render: (v?: string) => v || '—' },
    { title: t('creditNotes_Amount'), dataIndex: 'amount', align: 'right', render: (v: number) => formatCurrency(v) },
    { title: t('creditNotes_Balance'), dataIndex: 'balance', align: 'right', render: (v: number) => <b>{formatCurrency(v)}</b> },
    { title: t('creditNotes_IssuedOn'), dataIndex: 'issuedOn', render: formatDate },
    { title: t('creditNotes_IssuedBy'), dataIndex: 'issuedByName' },
    { title: t('creditNotes_ExpiresOn'), dataIndex: 'expiresOn', render: formatDay },
    { title: t('creditNotes_Status'), dataIndex: 'status', render: statusTag },
    { title: t('creditNotes_Uses'), dataIndex: 'useCount', align: 'right' },
    {
      key: 'detail',
      render: (_: any, item: CreditNoteListItemModel) => (
        <Button size="small" onClick={() => setDetailId(item.id)} data-testid={`credit-note-${item.number}`}>
          {t('creditNotes_Detail')}
        </Button>
      ),
    },
  ];

  const movementColumns = [
    { title: t('creditNotes_MovementOn'), dataIndex: 'on', render: formatDate },
    {
      title: t('creditNotes_MovementKind'),
      dataIndex: 'kind',
      render: (v: string) => <Tag color={v === 'use' ? 'blue' : 'green'}>{t(`creditNotes_Movement_${v}`)}</Tag>,
    },
    {
      title: t('creditNotes_Amount'),
      dataIndex: 'amount',
      align: 'right',
      render: (v: number, m: CreditNoteMovementModel) => `${m.kind === 'use' ? '-' : '+'}${formatCurrency(v)}`,
    },
    { title: t('creditNotes_BalanceAfter'), dataIndex: 'balanceAfter', align: 'right', render: (v: number) => formatCurrency(v) },
    { title: t('creditNotes_Booking'), dataIndex: 'bookingRefNo' },
    { title: t('creditNotes_Ticket'), dataIndex: 'ticketRefNo', render: (v?: string) => v || '—' },
    { title: t('creditNotes_By'), dataIndex: 'byName' },
  ];

  const note = detail?.creditNote;
  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('creditNotes_Explanation')} />
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          style={{ width: 220 }}
          placeholder={t('creditNotes_AllStatuses')}
          value={status}
          onChange={v => {
            setPage(1);
            setStatus(v);
          }}
          options={STATUSES.map(s => ({ value: s, label: t(`creditNotes_Status_${s}`) }))}
          data-testid="credit-notes-status"
        />
        <Input placeholder={t('creditNotes_Number')} value={number} onChange={e => setNumber(e.target.value)} onPressEnter={search} />
        <Input placeholder={t('creditNotes_Phone')} value={phone} onChange={e => setPhone(e.target.value)} onPressEnter={search} />
        <Button type="primary" onClick={search}>
          {t('creditNotes_Search')}
        </Button>
      </Space>
      <div style={{ marginBottom: 8 }} data-testid="credit-notes-totals">
        {t('creditNotes_Totals', {
          count: data?.total ?? 0,
          amount: formatCurrency(data?.totalAmount ?? 0),
          balance: formatCurrency(data?.totalBalance ?? 0),
        })}
      </div>
      <Table<CreditNoteListItemModel>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        columns={columns as any}
        pagination={{ current: page, pageSize: 50, total: data?.total ?? 0, onChange: setPage, showSizeChanger: false }}
        locale={{ emptyText: t('creditNotes_None') }}
        bordered
        size="middle"
      />
      <Modal
        open={!!detailId}
        width={980}
        title={note ? t('creditNotes_DetailTitle', { number: note.number }) : t('creditNotes_Detail')}
        onCancel={() => setDetailId(undefined)}
        footer={null}
        destroyOnClose
      >
        {note && (
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label={t('creditNotes_Holder')}>{note.holderName}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_Status')}>{statusTag(note.status)}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_Amount')}>{formatCurrency(note.amount)}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_Balance')}>{formatCurrency(note.balance)}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_IssuedOn')}>{`${formatDate(note.issuedOn)} · ${note.issuedByName || ''}`}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_ExpiresOn')}>{formatDay(note.expiresOn)}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_IssuedFor')}>{detail?.issuedFor || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('creditNotes_Source')}>{detail?.sourceNumber || '—'}</Descriptions.Item>
          </Descriptions>
        )}
        <Table<CreditNoteMovementModel>
          rowKey={(m, i) => `${m.on}-${i}`}
          loading={detailLoading}
          dataSource={detail?.movements || []}
          columns={movementColumns as any}
          pagination={false}
          locale={{ emptyText: t('creditNotes_NoMovement') }}
          size="small"
          bordered
        />
      </Modal>
    </MainContent>
  );
}
